import React, { useEffect, useState } from "react";
import {
  Wallet as WalletIcon,
  ArrowUpRight,
  ArrowDownLeft,
  Search,
  Filter,
  MoreHorizontal,
} from "lucide-react";
import { getWalletList } from "../../services/commonServices";
import { useSelector } from "react-redux";
import { data, useNavigate } from "react-router-dom";
import { decryptData } from "../../utils/crypto";
import { notify } from "../../components/Common/Alerts";
import Utils from "../../utils";
import { formatCurrencyINR } from "../../utils/function";
import { ChevronRight } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";

const Wallet = () => {
  let auth = useSelector((x) => x.auth.value);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [transactionsList, setTransactionsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!auth.active_store_id) return;
    setIsLoading(true);
    const data = {
      store_id: auth.active_store_id,
    };
    getWalletList(data)
      .then((response) => {
        const decrypt = decryptData(response.data.data);
        setTransactionsList(decrypt.data);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        notify.error(Utils.getErrorMessage(err));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [auth.active_store_id]);

  const filteredCustomers = transactionsList?.list?.filter((item) => {
    const name = item?.customer_name?.toLowerCase() || "";
    const mobile = item?.customer_mobile?.toString() || "";
    const value = search.toLowerCase();

    return name.includes(value) || mobile.includes(value);
  });

  return (
    <div className="p-4 md:p-6 bg-gray-50 dark:bg-slate-950 min-h-screen transition-colors duration-300">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-slate-100">
          {t("walletManagement")}
        </h1>
        <p className="text-sm text-gray-500 dark:text-slate-400">
          {t("trackStoreEarnings")}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-[#e66a15] rounded-2xl p-6 text-white shadow-lg shadow-orange-200 dark:shadow-none flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div className="p-2 bg-white/20 rounded-lg">
              <WalletIcon size={24} />
            </div>
            <span className="text-xs font-medium bg-white/20 px-2 py-1 rounded-full">
              {t("primaryWallet")}
            </span>
          </div>
          <div className="mt-4">
            <p className="text-white/80 text-sm">{t("totalBalance")}</p>
            <h2 className="text-3xl font-bold">
              {formatCurrencyINR(transactionsList?.total_balance || 0)}
            </h2>
          </div>
        </div>

        {/* Secondary Stats (Placeholder for your other cards) */}
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
        {/* Table Header Section */}
        <div className="p-4 md:p-6 border-b border-gray-100 dark:border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <h3 className="font-bold text-gray-800 dark:text-slate-200">
            {t("recentTransactions")}{" "}
          </h3>
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500"
              size={18}
            />
            <input
              type="text"
              placeholder={t("searchByNameOrMobile")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-[#e66a15] w-full md:w-80"
            />
          </div>
        </div>

        {/* DESKTOP VIEW: Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50 dark:bg-slate-800/50 text-gray-600 dark:text-slate-400 text-sm uppercase">
              <tr>
                <th className="px-6 py-4 font-semibold">{t("customerName")}</th>
                <th className="px-6 py-4 font-semibold">{t("date")}</th>
                <th className="px-6 py-4 font-semibold text-center">
                  {t("recentTransaction")}
                </th>
                <th className="px-6 py-4 font-semibold text-center">
                  {t("totalAmount")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {filteredCustomers?.length > 0 ? (
                filteredCustomers.map((item, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-50 dark:hover:bg-slate-800/30 transition-colors cursor-pointer"
                    onClick={() =>
                      navigate(`/wallet-details/${item.customer_id}`)
                    }
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#e66a15]/10 text-[#e66a15] flex items-center justify-center font-bold text-xs">
                          {item?.customer_name.charAt(0)}
                        </div>
                        <span className="font-medium text-gray-800 dark:text-slate-200">
                          {item?.customer_name}{" "}
                          <span className="text-gray-400 dark:text-slate-500 font-normal">
                            ({item.customer_mobile})
                          </span>
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-slate-400">
                      {item?.last_transaction?.created_at}
                    </td>
                    <td
                      className={`px-6 py-4 font-bold text-center ${
                        item?.last_transaction?.type?.toLowerCase() === "in"
                          ? "text-green-500 dark:text-green-400"
                          : item?.last_transaction?.type?.toLowerCase() ===
                              "out"
                            ? "text-red-500 dark:text-red-400"
                            : "text-gray-500 dark:text-slate-500"
                      }`}
                    >
                      {`${
                        item?.last_transaction?.type?.toLowerCase() === "in"
                          ? "+"
                          : item?.last_transaction?.type?.toLowerCase() ===
                              "out"
                            ? "-"
                            : ""
                      } ${formatCurrencyINR(Math.abs(Number(item?.last_transaction?.amount || 0)))}`}
                    </td>
                    <td
                      className={`px-6 py-4 text-center font-bold ${
                        Number(item.balance) > 0
                          ? "text-green-500 dark:text-green-400"
                          : Number(item.balance) < 0
                            ? "text-red-500 dark:text-red-400"
                            : "text-gray-500 dark:text-slate-500"
                      }`}
                    >
                      {formatCurrencyINR(item.balance)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    className="text-center py-10 text-gray-400 dark:text-slate-600"
                  >
                    {t("noCustomersFound")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* MOBILE VIEW: Card List */}
        <div className="md:hidden divide-y divide-gray-100 dark:divide-slate-800">
          {filteredCustomers?.length > 0 ? (
            filteredCustomers?.map((item, index) => (
              <div
                key={index}
                className="p-4 bg-white dark:bg-slate-900 flex items-center justify-between active:bg-gray-50 dark:active:bg-slate-800 transition-colors"
                onClick={() => navigate(`/wallet-details/${item.customer_id}`)}
              >
                {/* LEFT SIDE */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#e66a15]/10 text-[#e66a15] flex items-center justify-center font-bold">
                    {item?.customer_name?.charAt(0)}
                  </div>

                  <div className="flex flex-col">
                    <span className="font-bold text-gray-800 dark:text-slate-200 leading-tight">
                      {item?.customer_name}
                    </span>

                    <span className="text-xs text-gray-500 dark:text-slate-400">
                      {item.customer_mobile}
                    </span>

                    <span className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">
                      {item?.last_transaction?.created_at}
                    </span>
                  </div>
                </div>

                {/* RIGHT SIDE */}
                <div className="text-right flex flex-col items-end gap-1">
                  <span
                    className={`text-md font-bold ${
                      Number(item.balance) > 0
                        ? "text-green-500 dark:text-green-400"
                        : Number(item.balance) < 0
                          ? "text-red-500 dark:text-red-400"
                          : "text-gray-500 dark:text-slate-500"
                    }`}
                  >
                    {formatCurrencyINR(item.balance)}
                  </span>
                  <span
                    className={`font-black text-xs ${
                      (item?.last_transaction?.type || "").toLowerCase() ===
                      "in"
                        ? "text-green-600 dark:text-green-500"
                        : (item?.last_transaction?.type || "").toLowerCase() ===
                            "out"
                          ? "text-red-600 dark:text-red-500"
                          : "text-gray-500 dark:text-slate-500"
                    }`}
                  >
                    {(() => {
                      const type = (
                        item?.last_transaction?.type || ""
                      ).toLowerCase();
                      const amount = Number(
                        item?.last_transaction?.amount || 0,
                      );
                      return `${type === "in" ? "+" : type === "out" ? "-" : ""}${formatCurrencyINR(Math.abs(amount))}`;
                    })()}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-10 text-gray-400 dark:text-slate-600">
              {t("noCustomersFound")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Wallet;
